// src/components/exam-builder/CtestModal.jsx
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import { X, Plus, Trash2 } from "lucide-react";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";

// جایگزینی [] با {{1}}, {{2}}, ...
function replaceCtestPlaceholders(html, startIndex = 1) {
  if (typeof html !== "string") return { text: "", count: 0 };
  let i = startIndex - 1;
  const rx = /\[\s*\]/g;
  const text = html.replace(rx, () => {
    i += 1;
    return `{{${i}}}`;
  });
  return { text, count: i - (startIndex - 1) };
}

// برعکس: نمایش {{1}}، {{2}} به صورت [] برای ادیت
function unreplaceCtestPlaceholders(html) {
  if (typeof html !== "string") return "";
  return html.replace(/{{\s*\d+\s*}}/g, "[]");
}

const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  text_fa: "",
  text_en: "",
  blanks: [{ key: "1", answer: "" }],
  points: 1,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

const itemToForm = (item) => {
  if (!item) return makeEmptyForm();
  const stem = item.stem || {};
  const body = item.body || {};
  const text = body.text || {};

  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa: stem.fa || "",
    stem_en: stem.en || "",
    // متن ذخیره‌شده در سرور {{1}} دارد، برای UI به [] تبدیل می‌کنیم
    text_fa: unreplaceCtestPlaceholders(text.fa || ""),
    text_en: unreplaceCtestPlaceholders(text.en || ""),
    blanks:
      Array.isArray(body.blanks) && body.blanks.length
        ? body.blanks.map((b, idx) => ({
            key: String(b.key ?? idx + 1),
            answer: b.answer || "",
          }))
        : [{ key: "1", answer: "" }],
    points:
      typeof item.points === "number" && !Number.isNaN(item.points)
        ? item.points
        : 1,
    status: item.status || "published",
    tags: Array.isArray(item.tags) ? item.tags.join(",") : item.tags || "",
    explanation_fa,
    explanation_en,
  };
};

export default function CtestModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm());

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(itemToForm(item));
    } else {
      setForm(makeEmptyForm());
    }
  }, [isOpen, isEdit, item]);

  if (!isOpen) return null;

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const addBlank = () => {
    setForm((f) => ({
      ...f,
      blanks: [...f.blanks, { key: String(f.blanks.length + 1), answer: "" }],
    }));
  };

  const removeBlank = (idx) => {
    setForm((f) => ({
      ...f,
      blanks: f.blanks.filter((_, i) => i !== idx),
    }));
  };

  const handleBlankChange = (idx, value) => {
    setForm((f) => {
      const blanks = [...f.blanks];
      blanks[idx].answer = value;
      return { ...f, blanks };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.text_fa.trim() && !form.text_en.trim()) {
      toast.error(t("ctest.toasts.text_required"));
      return;
    }

    // تبدیل [] به {{1}}, {{2}}, ...
    const faConv = replaceCtestPlaceholders(form.text_fa, 1);
    const enConv = replaceCtestPlaceholders(form.text_en, 1);
    const blanksCount = Math.max(faConv.count, enConv.count);

    if (blanksCount === 0) {
      toast.error(t("ctest.toasts.no_placeholders"));
      return;
    }

    const normalizedBlanks = Array.from({ length: blanksCount }, (_, idx) => {
      const a = form.blanks[idx];
      const key = String(idx + 1);
      return {
        key,
        answer: (a?.answer || "").trim(),
      };
    });

    const payload = {
      kind: "c_test",
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: { fa: form.stem_fa || undefined, en: form.stem_en || undefined },
      body: {
        text: {
          fa: faConv.text || undefined,
          en: enConv.text || undefined,
        },
        blanks: normalizedBlanks,
      },
      points: Number(form.points) || 1,
      status: form.status || "draft",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t("ctest.toasts.updated", "سؤال C-Test به‌روزرسانی شد")
          : t("ctest.toasts.created")
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg md:text-xl font-semibold">{t("ctest.title")}</h2>
        <button className="p-2 rounded-lg hover:bg-gray-100" onClick={onClose}>
          <X />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Stem */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div dir={isRtl ? "rtl" : "ltr"}>
            <label className="block text-sm font-medium mb-1">
              {t("ctest.stem_fa")}
            </label>
            <CustomEditor
              value={form.stem_fa}
              onEditorChange={(c) => handleEditorChange("stem_fa", c)}
              placeholder={t("ctest.stem_fa_ph")}
              directionality={isRtl ? "rtl" : "ltr"}
            />
          </div>
          <div dir="ltr">
            <label className="block text-sm font-medium mb-1">
              {t("ctest.stem_en")}
            </label>
            <CustomEditor
              value={form.stem_en}
              onEditorChange={(c) => handleEditorChange("stem_en", c)}
              placeholder={t("ctest.stem_en_ph")}
            />
          </div>
        </div>

        {/* Text / Passage */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div dir={isRtl ? "rtl" : "ltr"}>
            <label className="block text-sm font-medium mb-1">
              {t("ctest.text_fa")}
            </label>
            <CustomEditor
              value={form.text_fa}
              onEditorChange={(c) => handleEditorChange("text_fa", c)}
              placeholder={t("ctest.text_fa_ph")}
            />
          </div>
          <div dir="ltr">
            <label className="block text-sm font-medium mb-1">
              {t("ctest.text_en")}
            </label>
            <CustomEditor
              value={form.text_en}
              onEditorChange={(c) => handleEditorChange("text_en", c)}
              placeholder={t("ctest.text_en_ph")}
            />
          </div>
        </div>

        {/* Blanks */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium">
              {t("ctest.blanks")}
            </label>
            <button
              type="button"
              onClick={addBlank}
              className="text-blue-600 text-sm"
            >
              {t("ctest.add_blank")}
            </button>
          </div>

          <div className="space-y-2">
            {form.blanks.map((b, idx) => (
              <div
                key={idx}
                className="border rounded-lg p-3 bg-gray-50 space-y-2"
              >
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold">
                    {t("ctest.blank_key", { key: b.key })}
                  </span>
                  {form.blanks.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeBlank(idx)}
                      className="p-1 rounded-full text-red-600 hover:bg-red-100"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>

                <input
                  type="text"
                  placeholder={t(
                    "ctest.answer_ph",
                    "حروف حذف‌شده (مثلاً uage)"
                  )}
                  value={b.answer}
                  onChange={(e) => handleBlankChange(idx, e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Meta Fields */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("ctest.points")}
            </label>
            <input
              type="number"
              min={0}
              className="w-full border rounded-lg px-3 py-2"
              value={form.points}
              onChange={(e) =>
                setForm((f) => ({ ...f, points: e.target.value }))
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("ctest.status")}
            </label>
            <select
              className="w-full border rounded-lg px-3 py-2"
              value={form.status}
              onChange={(e) =>
                setForm((f) => ({ ...f, status: e.target.value }))
              }
            >
              <option value="published">{t("common.published")}</option>
              <option value="draft">{t("common.draft")}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("ctest.tags")}
            </label>
            <input
              className="w-full border rounded-lg px-3 py-2"
              placeholder={t("common.tags_csv")}
              value={form.tags}
              onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
            />
          </div>
        </div>

        <ExplanationField
          valueFa={form.explanation_fa}
          valueEn={form.explanation_en}
          onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
          onChangeEn={(v) => handleEditorChange("explanation_en", v)}
          isRtl={isRtl}
        />

        <div className="mt-4">
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg inline-flex items-center gap-2"
          >
            <Plus size={18} />{" "}
            {isEdit ? t("common.update") : t("common.create")}
          </button>
        </div>
      </form>
    </>
  );
}
